import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Briefcase, Shield, Star, TrendingUp, Search, Filter, 
  ArrowRight, Code, Palette, ShoppingCart, Server, Globe,
  MessageSquare, BarChart, Settings, Zap, Package, Gift,
  CheckCircle, Clock, Users, Target
} from 'lucide-react';
import axios from 'axios';
import { API_URLS } from '../config/api';
import { createSlug } from '../lib/utils';

// Icon mapping
const iconMap = {
  Briefcase, Shield, Star, TrendingUp, Code, Palette, ShoppingCart, 
  Server, Globe, MessageSquare, BarChart, Settings, Zap, Package, 
  Gift, CheckCircle, Clock, Users, Target
};

const AllServicesPage = () => {
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchServices();
  }, []);

  useEffect(() => {
    filterServices();
  }, [services, searchQuery, selectedCategory]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_URLS.SERVICES);
      if (response.data.status === 'success') {
        const servicesData = response.data.data || [];
        setServices(servicesData);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(servicesData.map(s => s.category).filter(Boolean))];
        setCategories(uniqueCategories);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterServices = () => {
    let filtered = [...services];

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(service =>
        service.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        service.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(service => service.category === selectedCategory);
    }

    setFilteredServices(filtered);
  };


  const getBadgeVariant = (badge) => {
    if (!badge) return 'default';
    const badgeLower = badge.toLowerCase();
    
    if (badgeLower.includes('popüler') || badgeLower.includes('popular')) return 'default';
    if (badgeLower.includes('yeni') || badgeLower.includes('new')) return 'destructive';
    if (badgeLower.includes('önerilen') || badgeLower.includes('recommended')) return 'secondary';
    if (badgeLower.includes('ai')) return 'secondary';
    
    return 'default';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-purple-600 to-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium">
              <Briefcase className="w-5 h-5" />
              <span>Tüm Hizmetlerimiz</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              İşletmeniz İçin
              <span className="block mt-2">Komple Dijital Çözümler</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-purple-100 max-w-3xl mx-auto">
              Freelance projelerden e-ticaret paketlerine, hosting hizmetlerinden AI destekli çözümlere kadar ihtiyacınız olan her şey
            </p>
          </div>
        </div>
      </section>

      {/* Main Content with Sidebar */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Filters - Wrap Layout */}
          <div className="lg:hidden space-y-4 mb-6">
            {/* Mobile Search */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Hizmet Ara
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Hizmet ara..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 py-2 border-2 border-gray-200 focus:border-purple-500 w-full"
                />
              </div>
            </div>

            {/* Mobile Categories */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2 px-1 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Kategoriler
              </h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedCategory('all')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    selectedCategory === 'all'
                      ? 'bg-purple-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>Tüm Hizmetler</span>
                  <Badge variant="secondary" className={selectedCategory === 'all' ? 'bg-white text-purple-600' : 'bg-white'}>
                    {services.length}
                  </Badge>
                </button>
                {categories.map((category, index) => {
                  const count = services.filter(s => s.category === category).length;
                  return (
                    <button
                      key={index}
                      onClick={() => setSelectedCategory(category)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === category
                          ? 'bg-purple-600 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{category}</span>
                      <Badge variant="secondary" className={selectedCategory === category ? 'bg-white text-purple-600' : 'bg-white'}>
                        {count}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Sidebar - Search & Filter - Desktop Only */}
            <aside className="hidden lg:block lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-4 space-y-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Search className="w-5 h-5 text-purple-600" />
                    Hizmet Ara
                  </h3>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <Input
                      type="text"
                      placeholder="Hizmet ara..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 py-2 border-2 border-gray-200 focus:border-purple-500 w-full"
                    />
                  </div>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Filter className="w-5 h-5 text-purple-600" />
                    Kategoriler
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                        selectedCategory === 'all'
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>Tüm Hizmetler</span>
                        <span className={`text-sm ${selectedCategory === 'all' ? 'text-purple-200' : 'text-gray-500'}`}>
                          ({services.length})
                        </span>
                      </div>
                    </button>
                    {categories.map((category, index) => {
                      const count = services.filter(s => s.category === category).length;
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedCategory(category)}
                          className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                            selectedCategory === category
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span>{category}</span>
                            <span className={`text-sm ${selectedCategory === category ? 'text-purple-200' : 'text-gray-500'}`}>
                              ({count})
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Results count */}
                <div className="border-t pt-6">
                  <p className="text-sm text-gray-600 text-center">
                    <span className="font-semibold text-purple-600 text-lg">{filteredServices.length}</span>
                    <br />
                    hizmet bulundu
                    {searchQuery && (
                      <span className="block mt-1">"{searchQuery}" için</span>
                    )}
                  </p>
                </div>
              </div>
            </aside>

            {/* Right Content - Services Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="text-center py-20">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600 text-lg">Hizmetler yükleniyor...</p>
                </div>
              ) : filteredServices.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Search className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Hizmet Bulunamadı</h3>
                  <p className="text-gray-600 mb-6">
                    {searchQuery 
                      ? `"${searchQuery}" için sonuç bulunamadı. Lütfen farklı bir arama yapın.`
                      : 'Seçili kategoride hizmet bulunamadı.'}
                  </p>
                  <Button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}>
                    Tüm Hizmetleri Göster
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredServices.map((service, index) => {
                    const iconName = service.icon;
                    const IconComponent = (typeof iconName === 'string' && iconMap[iconName]) ? iconMap[iconName] : Briefcase;
                    const serviceKey = service.id || service.title || `service-${index}`;
                    
                    return (
                      <Link
                        key={serviceKey}
                        to={`/hizmet/${service.slug || createSlug(service.title || service.name || 'default')}`}
                        className="block group"
                      >
                        <Card className="h-full border-0 shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all duration-500 overflow-hidden bg-white">
                          {/* Badge */}
                          {service.badge && (
                            <div className="absolute top-3 right-3 z-10">
                              <Badge variant={getBadgeVariant(service.badge)} className="text-xs font-medium shadow-lg">
                                {service.badge}
                              </Badge>
                            </div>
                          )}

                          {/* Animated Background Gradient */}
                          <div className={`absolute inset-0 bg-gradient-to-br ${service.color || 'from-purple-500 to-blue-500'} opacity-0 group-hover:opacity-10 transition-opacity duration-500`}></div>

                          <CardContent className="p-6 space-y-4 relative z-10">
                            {/* Icon */}
                            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.color || 'from-purple-500 to-blue-500'} flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                              {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                            </div>

                            {/* Service Info */}
                            <div className="space-y-2">
                              <h3 className="text-xl font-bold text-gray-900 group-hover:text-purple-700 transition-colors duration-300 line-clamp-2">
                                {service.title}
                              </h3>
                              
                              {service.category && (
                                <Badge variant="outline" className="text-xs">
                                  {service.category}
                                </Badge>
                              )}
                              
                              <p className="text-gray-600 leading-relaxed line-clamp-3 text-sm">
                                {service.description}
                              </p>
                            </div>

                            {/* CTA */}
                            <div className="pt-4 border-t border-gray-100">
                              <div className="flex items-center justify-between text-purple-600 group-hover:text-purple-700 font-medium">
                                <span>Detayları Gör</span>
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-300" />
                              </div>
                            </div>

                            {/* Features count if available */}
                            {service.features && Array.isArray(service.features) && service.features.length > 0 && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <CheckCircle className="w-4 h-4 text-green-500" />
                                <span>{service.features.length} özellik</span>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto">
            <MessageSquare className="w-8 h-8" />
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold">
            Aradığınız Hizmeti Bulamadınız mı?
          </h2>
          
          <p className="text-xl text-purple-100">
            Özel gereksinimleriniz için kişiselleştirilmiş çözümler sunuyoruz. Uzman ekibimizle iletişime geçin!
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Link to="/iletisim">
              <Button size="lg" className="bg-white text-purple-600 hover:bg-gray-100 transform hover:scale-105 transition-all duration-300">
                <MessageSquare className="w-5 h-5 mr-2" />
                İletişime Geç
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            
            <Link to="/">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 transform hover:scale-105 transition-all duration-300">
                Ana Sayfaya Dön
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AllServicesPage;


